/**
 * About CV PDF — 가로(Landscape) A4 (842×595 pt).
 * 본문 구조는 DocumentVerticalCV와 동일, 여백·폭만 가로에 맞게 조정.
 */

import React from 'react';
import { Document, Page, View, StyleSheet, Image } from '@react-pdf/renderer';
import { AboutContentMode, CareerType } from '../../shared/enum/EnumRepository';
import { CareerData, MemberInfoData } from '../../shared/dto/EntityRepository';
import { buildCvCareerBlocks, CvPdfText } from './cvPdfShared';
import { getCareerCurrentLabel } from '../../shared/util/careerCurrentLabel';
import logo from '../../asset/gnb/logo_qr_transparent.png';

/** A4 landscape (pt) */
const A4_LANDSCAPE: [number, number] = [842, 595];

const CV_INFORMATION_CONFIRM_EN =
  'I confirm that all the information provided above is accurate to the best of my knowledge.';

interface DocumentHorizontalCVProps {
  memberInfo: MemberInfoData;
  careerList: CareerData[];
  careerSectionOrder?: readonly CareerType[];
  contentMode?: AboutContentMode;
  /**
   * true: 이 Document의 모든 PDF 페이지 우상단에 로고(단독 CV 다장).
   * false: 이 `<Page>`에만 그림 — 상위 PDF에 이미 장마다 로고가 있을 때 중복 방지.
   */
  logoDocumentFixed?: boolean;
  /**
   * true: 이 Document의 모든 장에 쪽번호(wrap 다장).
   * false: 상위 PDF에 이미 장마다 쪽번호가 있을 때.
   */
  pageNumberDocumentFixed?: boolean;
}

/** 단일 가로 CV 페이지 (프로젝트 PDF 등 다른 Document 안에 합치기용) */
export const HorizontalCVPdfPage: React.FC<DocumentHorizontalCVProps> = ({
  memberInfo,
  careerList,
  careerSectionOrder,
  contentMode = AboutContentMode.Orig,
  logoDocumentFixed = true,
  pageNumberDocumentFixed = true,
}) => {
  const isEng = contentMode === AboutContentMode.ENG;
  const displayName = isEng ? memberInfo.nameEn ?? '' : memberInfo.name;
  const displayDescription = isEng
    ? memberInfo.descriptionEn ?? ''
    : memberInfo.description;
  const careerText = (c: CareerData) =>
    isEng ? c.contentEn ?? '' : c.content ?? '';

  const pdfStyles = { Section: PDFStyles.Section, Text: PDFStyles.Text };

  return (
    <Page size={A4_LANDSCAPE} style={PDFStyles.Page.root}>
      <View style={PDFStyles.Section.root}>
        <View style={PDFStyles.Section.profileAndInfo}>
          <View style={PDFStyles.Section.infoContainer}>
            <View style={PDFStyles.Section.nameRow}>
              <CvPdfText style={PDFStyles.Text.name}>{displayName}</CvPdfText>
              <CvPdfText style={PDFStyles.Text.inlineMeta}>
                {` (b. ${memberInfo.year}, ${memberInfo.country})`}
              </CvPdfText>
            </View>
          </View>
        </View>

        <View style={PDFStyles.Section.description}>
          <CvPdfText style={PDFStyles.Text.description}>{displayDescription}</CvPdfText>
        </View>

        {careerList && (
          <View style={PDFStyles.Section.careerContainer}>
            {buildCvCareerBlocks(
              careerList,
              careerText,
              pdfStyles,
              careerSectionOrder,
              (career) =>
                getCareerCurrentLabel(career.currentLabelOrig, contentMode),
            )}
          </View>
        )}
      </View>
      <View style={PDFStyles.Section.pageLogo} fixed={logoDocumentFixed}>
        <Image source={logo} style={PDFStyles.Image.logo} cache />
      </View>
      <View style={PDFStyles.Section.informationConfirm} fixed>
        <CvPdfText
          style={PDFStyles.Text.informationConfirm}
          render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
            pageNumber === totalPages ? CV_INFORMATION_CONFIRM_EN : ''
          }
        />
      </View>
      <View style={PDFStyles.Section.pageNumber} fixed={pageNumberDocumentFixed}>
        <CvPdfText
          style={PDFStyles.Text.pageNumber}
          render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
            `${pageNumber} / ${totalPages}`
          }
        />
      </View>
    </Page>
  );
};

const DocumentHorizontalCV: React.FC<DocumentHorizontalCVProps> = (props) => (
  <Document>
    <HorizontalCVPdfPage {...props} />
  </Document>
);

const PDFTypography = {
  H1: {
    fontSize: 13,
    fontFamily: 'NotoKR',
    fontWeight: 600,
  },
  InlineMemberInfo: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    color: '#6B6B6B',
  },
  MemberInfoBodySmall: {
    fontSize: 7.5,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  CareerTitle: {
    fontSize: 9,
    fontFamily: 'NotoKR',
    fontWeight: 500,
  },
  CareerBody: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    lineHeight: 1.3,
  },
  PageNumber: {
    fontSize: 6.5,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  InformationConfirm: {
    fontSize: 6.5,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    color: 'lightgray',
  },
};

const PDFStyles = {
  Page: StyleSheet.create({
    root: {
      paddingTop: 36,
      paddingBottom: 52,
      paddingLeft: 44,
      paddingRight: 44,
      backgroundColor: '#ffffff',
      fontFamily: 'NotoKR',
      fontWeight: 400,
    },
  }),

  Image: StyleSheet.create({
    logo: {
      width: 24,
      height: 24,
      objectFit: 'contain' as const,
    },
  }),

  Section: StyleSheet.create({
    root: {},
    profileAndInfo: {
      marginBottom: 14,
    },
    infoContainer: {
      width: '100%',
    },
    nameRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'flex-end',
      marginBottom: 8,
    },
    description: {
      width: '58%',
    },
    careerContainer: {
      marginTop: 18,
      alignSelf: 'flex-start',
      width: '100%',
    },
    careerItem: {
      flexDirection: 'row',
      marginBottom: 3,
    },
    careerContentWrap: {
      flex: 1,
      minWidth: 0,
    },
    pageLogo: {
      position: 'absolute',
      top: 5,
      right: 16,
    },
    informationConfirm: {
      position: 'absolute',
      bottom: 40,
      right: 36,
      textAlign: 'right',
    },
    pageNumber: {
      position: 'absolute',
      bottom: 15,
      left: 0,
      right: 20,
      textAlign: 'right',
    },
  }),

  Text: StyleSheet.create({
    name: {
      ...PDFTypography.H1,
    },
    inlineMeta: {
      ...PDFTypography.InlineMemberInfo,
      marginLeft: 4,
    },
    description: {
      ...PDFTypography.MemberInfoBodySmall,
      lineHeight: 1.45,
    },
    careerTitle: {
      ...PDFTypography.CareerTitle,
      width: '50%',
      marginBottom: 6,
      borderBottomWidth: 0.5,
      borderBottomColor: '#000',
      borderBottomStyle: 'solid',
    },
    careerTitleSectionGap: {
      marginTop: 16,
    },
    careerYear: {
      ...PDFTypography.CareerBody,
      width: 88,
    },
    careerYearStart: {
      ...PDFTypography.CareerBody,
      width: 30,
    },
    separator: {
      ...PDFTypography.CareerBody,
      width: 12,
    },
    careerYearEnd: {
      ...PDFTypography.CareerBody,
      width: 44,
    },
    careerContent: {
      ...PDFTypography.CareerBody,
    },
    pageNumber: {
      ...PDFTypography.PageNumber,
    },
    informationConfirm: {
      ...PDFTypography.InformationConfirm,
    },
  }),
};

export default DocumentHorizontalCV;
